
import CustomModal from '~/components/ui/molecule/CustomModal.vue';
import { mapActions, mapGetters } from 'vuex';
import CloseIcon from '../icons/CloseIcon.vue';
import SearchSymbolWrapper from '~/components/stocks/SearchSymbolWrapper.vue';
import StockOperationCard from '../molecule/StockOperationCard.vue';
import { isMobileResponsive } from '~/utils/mixins';
import { getTransactableSymbolSearchTypes } from '~/integrations/api/transactable-symbol-search';

export default {
  name: 'OrderBookModal',
  components: {
    CustomModal,
    CloseIcon,
    SearchSymbolWrapper,
    StockOperationCard,
  },
  mixins: [isMobileResponsive],
  data() {
    return {
      error: '',
      price: 0,
      quantity: null,
      minQuantity: 0.000001,
      maxTotal: 10000000,
      daysToSubstract: 0,
      maxDate: null,
      formValues: {
        operationType: 'Buy',
        portfolioName: '',
        date: null,
        quantity: 1,
        chosenCurrency: '',
        chosenCurrencyCash: '',
      },
      total: 0,
      totalText: '0',
      conversionRate: 1,
      formIsMultiple: false,
      cashAvailableText: {},
      reset: false,
      symbolToSell: null,
      focusTickerSearchField: false,
      totalIsUpdating: false,
      cashAvailableAtDate: {},
      availablePortfolioCurrencies: [],
      tradingFee: 150,
      tradingFeeCurrency: '€',
      buttonState: 'idle',
    };
  },
  async fetch() {
    if (process.client) {
      if (!this.createOrderTransactionModal.portfolio) {
        await this.getMyPortfolios();

        await this.getPortfolioById(
          this.getMyPortfoliosRes.data.portfolios[0].id
        );
        this.setCreateOrderModalPortfolio(this.getPortfolioByIdRes.data);
      }
    }
  },
  computed: {
    ...mapGetters('modals', [
      'createOrderTransactionModal',
      'showNoDataAtDateError',
    ]),
    ...mapGetters('portfolio', [
      'getPortfolioByIdRes',
      'getMyPortfoliosRes',
      'createOperationRes',
    ]),
    // The symbol search should only include symbols that can be transacted (see domain/transactions-behavior.ts).
    transactableSymbolSearchTypes() {
      return getTransactableSymbolSearchTypes();
    },
    bodyStyle() {
      return this.isMobileResponsive ? {} : { height: '692px' };
    },
    defaultPortfolio() {
      return this.createOrderTransactionModal?.portfolio || null;
    },
    cashAvailable() {
      return this.cashAvailableAtDate || {};
    },
    shouldShowTickerSearchComponent() {
      return (
        this.createOrderTransactionModal.symbol === undefined ||
        this.createOrderTransactionModal.symbol === null
      );
    },
    getErrorForBuy() {
      if (this.quantity && this.quantity < this.minQuantity) {
        return this.$t('field_operation_quantity_min');
      } else if (this.total > this.maxTotal) {
        return this.$t('field_operation_total_max');
      } else if (this.hasInsufficientCash) {
        return this.$t('not_enough_cash_error_msg');
      }
      return null;
    },
    getErrorForSell() {
      if (this.quantity < this.minQuantity) {
        return this.$t('field_operation_quantity_min');
      } else if (this.quantity > this.maxQuantityToSell) {
        return this.$t('not_enough_shares');
      } else if (this.total > this.maxTotal) {
        return this.$t('field_operation_total_max');
      }
      return null;
    },
    maxQuantityToSell() {
      let quantity = 0;
      let operations = [];
      let sellOperations = [];
      const formDate = this.$dayjs(this.formValues.date);
      if (this.symbolToSell) {
        operations = this.symbolToSell.operations.filter((o) => {
          return (
            o.operationType == 'Buy' &&
            (this.$dayjs(o.operationDate).isBefore(formDate) ||
              this.$dayjs(o.operationDate).isSame(formDate, 'day'))
          );
        });
        sellOperations = this.symbolToSell.operations.filter((o) => {
          return (
            o.operationType == 'Sell' &&
            (this.$dayjs(o.operationDate).isBefore(formDate) ||
              this.$dayjs(o.operationDate).isSame(formDate, 'day'))
          );
        });
      } else if (this.createOrderTransactionModal.symbol) {
        operations = this.createOrderTransactionModal.symbol.operations.filter(
          (o) => {
            return (
              o.operationType == 'Buy' &&
              (this.$dayjs(o.operationDate).isBefore(formDate) ||
                this.$dayjs(o.operationDate).isSame(formDate, 'day'))
            );
          }
        );
        sellOperations =
          this.createOrderTransactionModal.symbol.operations.filter((o) => {
            return (
              o.operationType == 'Sell' &&
              (this.$dayjs(o.operationDate).isBefore(formDate) ||
                this.$dayjs(o.operationDate).isSame(formDate, 'day'))
            );
          });
      }
      if (!operations.length) {
        return 0;
      }
      quantity += operations?.reduce((n, { quantity }) => n + quantity, 0) ?? 0;
      quantity -=
        sellOperations?.reduce((n, { quantity }) => n + quantity, 0) ?? 0;

      return quantity;
    },
    cashOperationsPermitted() {
      if (!this.defaultPortfolio.league) {
        return true;
      }

      return this.defaultPortfolio.league.cashOperationsPermitted;
    },
    currency() {
      const defaultCurrency = 'EUR';

      if (
        this.createOrderTransactionModal &&
        this.createOrderTransactionModal.currency
      ) {
        return this.createOrderTransactionModal.currency;
      }

      if (
        this.createOrderTransactionModal &&
        this.createOrderTransactionModal.portfolio
      ) {
        return this.createOrderTransactionModal.portfolio.currency;
      }

      return defaultCurrency;
    },
    isConfirmDisabled() {
      if (this.buttonState === 'loading') return true;

      if (!this.createOrderTransactionModal.symbol) return true;

      if (!this.quantity || this.quantity <= 0) return true;

      if (this.formValues.operationType === 'Buy' && this.getErrorForBuy) {
        return true;
      }

      if (this.formValues.operationType === 'Sell' && this.getErrorForSell) {
        return true;
      }

      return false;
    },

    isSecondaryDisabled() {
      return this.buttonState === 'loading';
    },
  },
  watch: {
    defaultPortfolio(newVal) {
      if (!newVal || !this.formValues.date) return;
      this.getCashAvailable();
    },
    quantity(newVal) {
      if (this.totalIsUpdating) return;
      this.totalIsUpdating = true;

      const q = parseFloat(newVal) || 0;
      const p = parseFloat(this.price) || 0;
      const c = parseFloat(this.conversionRate) || 1;

      this.total = p * q * c;

      this.totalIsUpdating = false;
    },

    total(newVal) {
      if (this.totalIsUpdating) return;
      this.totalIsUpdating = true;

      const p = parseFloat(this.price) || 0;
      const c = parseFloat(this.conversionRate) || 1;

      this.quantity = p > 0 ? parseFloat(newVal) / (p * c) : 0;

      this.getCashAvailableText();

      this.totalIsUpdating = false;
    },

    'formValues.date'(newVal) {
      if (!newVal || !this.$dayjs(newVal).isValid()) return;
      if (!this.getPortfolioByIdRes?.data) return;
      this.getCashAvailable();
    },

    cashAvailableAtDate: {
      deep: true,
      immediate: true,
      handler() {
        this.getCashAvailableText();
        this.getDefaultChosenCurrency();
      },
    },

    'formValues.chosenCurrency'() {
      this.getCashAvailableText();
    },
  },

  mounted() {
    if (
      this.createOperationRes?.loading ||
      this.addPortfolioItemRes?.loading ||
      this.validatePortfolioOperationRes?.loading
    ) {
      showTransactionPendingToast(this.$t('transaction_processing'));
    }
  },
  created() {
    this.getDefaultChosenCurrency();
    this.getConversionRate();
    this.getMaxDate();
    this.formValues.date = this.maxDate;
    this.mitigatePriceCallback();
  },
  methods: {
    getOperationButtonClass(operationType) {
      if (this.formValues.operationType == operationType) {
        return 'bg-primary-light text-primary';
      } else {
        return 'text-neutral-7';
      }
    },
    getMaxDate() {
      const today = this.$dayjs().$d.getDay();
      let toSubstract = 0;

      if (today === 6) {
        toSubstract = 1;
      } else if (today === 0) {
        toSubstract = 2;
      }

      this.daysToSubstract = toSubstract;
      this.maxDate = this.$dayjs()
        .subtract(toSubstract, 'day')
        .format('YYYY-MM-DD');
    },

    handleOperationSwitch(operation) {
      if (operation === 'Buy') {
        this.formValues.operationType = 'Buy';
        this.resetModalState();
        this.removeSymbolFromOrderOperation();
      } else if (operation === 'Sell') {
        this.formValues.operationType = 'Sell';
        this.resetModalState();
        this.removeSymbolFromOrderOperation();
      }
    },
    getDefaultChosenCurrency() {
      if (!this.defaultPortfolio) {
        this.formValues.chosenCurrency = this.currency;
        return;
      }
      if (this.currency in this.cashAvailable) {
        this.formValues.chosenCurrency = this.currency;
      } else {
        const keys = Object.keys(this.cashAvailable);
        if (keys.length > 0) {
          this.formValues.chosenCurrency = keys[0];
        }
      }
    },
    resetModalState() {
      this.price = 0;
      this.quantity = null;
      this.symbolToSell = null;
      this.createOrderTransactionModal.symbol = null;
      this.createOrderTransactionModal.tickerValue = null;
      this.maxDate = this.$dayjs()
        .subtract(this.daysToSubstract, 'day')
        .format('YYYY-MM-DD');
    },
    handleOrderModalClose() {
      this.setNoDataAtDateError(false);

      const payload = {
        symbol: null,
        portfolioType: null,
        show: false,
        currency: '',
      };

      this.showCreateOrderOperationModal(payload);
    },
    handleAddSymbol(symbol) {
      this.symbolToSell = symbol;
      this.setOrderModalSymbol(symbol);

      this.$nextTick(() => {
        this.initPriceAndTotals(symbol);
      });
    },

    async handleSymbolChange(symbol, callback) {
      this.setOrderModalSymbol(symbol);
      await this.handleDateChange(this.formValues.date, callback);
    },

    initPriceAndTotals(symbol) {
      const basePrice = symbol?.close ?? 0;
      this.price = Math.round(basePrice * 100) / 100;

      this.quantity = null;
      this.total = 0;

      this.getConversionRate();
    },

    async handleDateChange(date, callback) {
      if (
        !this.createOrderTransactionModal.portfolio ||
        this.formdate == date
      ) {
        return;
      }
      this.formDate = date;
      this.getCashAvailable();
      if (!this.createOrderTransactionModal.symbol) return;
      const symbolName = this.createOrderTransactionModal.symbol.name
        ? this.createOrderTransactionModal.symbol.name
        : this.createOrderTransactionModal.symbol.symbol;
      const payload = {
        symbol: symbolName,
        date: date,
        portfolioId: this.createOrderTransactionModal.portfolio.id,
        forceEodUpdate: true,
      };
      await (this.isCrypto
        ? this.getCryptoTickerStockValue(payload)
        : this.getTickerStockValue(payload));
      this.valueLoading = false;
      typeof callback === 'function' && callback(true);
    },

    mitigatePriceCallback() {
      if (!this.tickerToAdd) return;

      const basePrice = this.tickerToAdd.close ?? 0;
      this.price = Math.round(basePrice * 100) / 100;
    },
    handleSymbolRemove() {
      this.$emit('onSymbolRemove');
      this.resetModalState();
      this.focusTickerSearchField = true;
    },

    getQuantityHelp() {
      return this.$t('asset_quantity_available', {
        quantity: this.maxQuantityToSell.toFixed(6),
      });
    },

    getChosenCurrencyLabel() {
      const currency = this.formValues.chosenCurrency;
      if (
        currency === null ||
        currency === undefined ||
        currency.length !== 3
      ) {
        return '';
      }
      const symbol = this.$display.money.getCurrencySymbol(currency);
      if (symbol !== '') {
        return symbol;
      }
      return currency;
    },

    getCashAvailableText() {
      const toReturn = {};
      const cashAvailable = this.cashAvailable;
      const chosenCurrency = this.formValues.chosenCurrency;

      for (const [key, value] of Object.entries(cashAvailable)) {
        let text = this.$display.money.money(
          value.value,
          value.symbol ?? 'USD',
          0,
          2
        );

        if (
          this.formValues.operationType === 'Buy' &&
          chosenCurrency === key &&
          value.value < this.total &&
          this.cashOperationsPermitted
        ) {
          text +=
            ' (' +
            this.$t('cash_will_be_added', {
              amount: this.$display.money.money(
                this.total - value.value,
                value.symbol ?? 'USD',
                0,
                2
              ),
            }) +
            ')';
        }

        toReturn[key] = text;
      }

      this.cashAvailableText = toReturn;
    },

    getCashAvailable() {
      if (!this.getPortfolioByIdRes?.data) return;
      if (!this.formValues.date || !this.$dayjs(this.formValues.date).isValid())
        return;

      const currency = this.currency;
      const cashItems = this.getPortfolioByIdRes.data.cashItems;
      const nextDay = this.$dayjs(this.formValues.date).add(1, 'day');

      this.cashAvailableAtDate = {};
      this.availablePortfolioCurrencies = [];

      cashItems.forEach((ci) => {
        const ciCurrencySymbol = this.$display.money.getCurrencySymbol(
          ci.currencyCode
        );

        const availableValueAtDate = ci.operations
          .filter((o) => this.$dayjs(o.operationDate).isBefore(nextDay))
          .reduce((sum, op) => sum + op.operationAmount, 0);

        this.cashAvailableAtDate[ci.currencyCode] = {
          value: availableValueAtDate,
          symbol: ciCurrencySymbol,
          conversion:
            currency !== ci.currencyCode
              ? availableValueAtDate *
                ci.latestCashItemToPortfolioCurrencyConversionRate
              : availableValueAtDate,
        };

        this.availablePortfolioCurrencies.push(ci.currencyCode);
      });
    },

    getDefaultCurrency() {
      const defaultCurrency = 'EUR';
      if (this.createOrderTransactionModal.currency !== '') {
        return this.createOrderTransactionModal.currency;
      }
      return this.createOrderTransactionModal.portfolio
        ? this.createOrderTransactionModal.portfolio.currency
        : defaultCurrency;
    },

    getConversionRate() {
      if (!this.symbolToSell || !this.formValues.chosenCurrency) {
        this.conversionRate = 1;
        return;
      }
      if (
        this.symbolToSell.currency === this.formValues.chosenCurrency ||
        (!('conversions' in this.symbolToSell) &&
          !('conversion' in this.symbolToSell))
      ) {
        this.conversionRate = 1;
      } else {
        if (this.symbolToSell.conversions?.length) {
          const conversionObject = this.symbolToSell.conversions.find(
            (c) => c.currency === this.formValues.chosenCurrency
          );
          this.conversionRate = conversionObject?.rate ?? 1;
        } else if (
          this.symbolToSell.conversion?.currency ===
          this.formValues.chosenCurrency
        ) {
          this.conversionRate = this.symbolToSell.conversion.rate ?? 1;
        } else {
          this.conversionRate = 1;
        }
      }
    },
    ...mapActions('modals', [
      'removeSymbolFromOrderOperation',
      'setCreateOrderModalPortfolio',
      'showCreateOrderOperationModal',
      'setNoDataAtDateError',
      'setOrderModalSymbol',
      'getCryptoTickerStockValue',
      'getTickerStockValue',
    ]),
    ...mapActions('portfolio', ['getMyPortfolios', 'getPortfolioById']),
  },
};
